<template>
  <div class="payment-form">
    <v-container grid-list-lg>
      <v-layout>
        <v-flex
          xs12
          sm10
          offset-sm1
          md8
          offset-md2>

          <div id="payment-form--alerts">
            <PaymentFormAlerts
              v-if="hasErrors"
              :api-error="apiError"
              :form-errors="alertFormErrors"
              @close="clearErrors" />
          </div>

          <div id="payment-form--intro">
            <!-- form intro !-->
            <v-layout row wrap>
              <v-flex xs12 text-xs-center class="header">
                <h1>Make a Payment</h1>
                <p>
                  Please review and complete the information below.<br/>
                  <small>* Required</small>
                </p>
              </v-flex>
            </v-layout>
          </div>

          <PaymentFormFinancials
            :payment-amount="paymentDefaults.paymentAmount"
            :isPayrix="isPayrix()"
            :invoices="paymentDefaults.invoices"
            :additionalCharge="additionalChargesSetting.value"
            :additionalChargeEnabled="additionalChargesSetting.enabled"
            :apiError="apiError"
            @cardTypeChange="cardTypeChange"/>

          <!-- Technology Fee Message -->
          <div
            v-if="technologyFeeFF && additionalChargesSetting.enabled"
            class="technology-fee-message">
            <v-layout row wrap>
              <v-flex xs12>
                <div class="technology-fee-content">
                  <p>
                    A {{ (additionalChargesSetting.value / 100).toFixed(2) }}% Technology Fee is
                    applied to card payments to help cover costs.
                    Contact your inspector for a fee-free payment option (such as cash or check).
                  </p>
                </div>
              </v-flex>
            </v-layout>
          </div>

          <v-layout row wrap>
            <v-flex xs12>
              <hr/>
            </v-flex>
          </v-layout>

          <div id="payment-form--inputs">
            <form @submit.prevent="submit" ref="paymentForm" novalidate>
              <!-- FI form fields !-->
              <div class="form-section">
                <v-layout row wrap>
                  <v-flex xs12>
                    <PaymentFormFinancialInstrument
                      v-if="isLegacyGateway"
                      v-model="model.paymentCard"
                      :payment-types="paymentDefaults.paymentTypes"/>
                    <PaymentFormFinancialInstrumentPayrix
                      ref="paymentProcessor"
                      v-if="!isLegacyGateway"
                      :merchant-id="paymentGateway.body.merchantId"
                      :payment-amount="paymentAmount"
                      :zip-code.sync="model.customer.postalCode"
                      :email.sync="model.customer.email"
                      :phone.sync="model.customer.phone" />
                  </v-flex>
                </v-layout>
              </div>

              <!-- customer form fields - only for legacy gateway !-->
              <div class="form-section" v-if="isLegacyGateway">
                <v-layout row wrap>
                <v-flex xs12>
                  <PaymentFormCustomer v-model="model.customer" />
                </v-flex>
              </v-layout>
              </div>

              <!-- submission controls !-->
              <div class="form-section">
                <v-layout row wrap>
                <v-flex xs12 class="send-payment">

                  <p>
                    <BaseButton
                      :hg-label="submitButtonText"
                      :loading="disableSubmitButton"
                      class="submit-button"
                      inline
                      type="submit"
                      ref="paymentSubmitBtn" />
                    <br/>

                    <router-link
                      :to="{ name: 'myInspections' }"
                      class="cancel-link btn-tertiary">
                      Cancel
                    </router-link>
                  </p>

                  <p>
                    Refunds/Returns are not accepted unless otherwise specified by the merchant.
                    Please contact the merchant for additional information.
                  </p>

                  <p>
                    By using our site, you agree to our
                    <a href="https://www.homegauge.com/hg-terms.html" target="_blank">Terms of Use</a>,
                    <a href="https://www.homegauge.com/resources/payment-security-notice" target="_blank">Payment Security Notice</a>,
                    and <a href="https://www.homegauge.com/privacy.html" target="_blank">Privacy Policy</a>.
                  </p>

                  <p>
                    <span class="questions">
                      Questions?
                    </span>
                    <template v-if="company.phone">
                      Call
                      <a :href="'tel:' + company.phoneLink">{{company.phone}}</a>
                    </template>
                    <template v-if="company.phone && company.email">
                      or
                    </template>
                    <template v-if="company.email">
                      email
                      <a :href="'mailto:' + company.email">{{company.email}}</a>
                    </template>
                  </p>
                </v-flex>
              </v-layout>
              </div>
            </form>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { Components, Mixins } from '@homegauge/vue';
import { mapState } from 'vuex';
import Moment from 'moment';

import PaymentFormAlerts from './PaymentFormAlerts.vue';
import PaymentFormCustomer from './PaymentFormCustomer.vue';
import PaymentFormFinancialInstrument from './PaymentFormFinancialInstrument.vue';
import PaymentFormFinancialInstrumentPayrix from './PaymentFormFinancialInstrumentPayrix.vue';
import PaymentFormFinancials from './PaymentFormFinancials.vue';
import featureFlags from '../../constants/feature-flags';

const { BaseButton } = Components;
const { CurrencyFilter } = Mixins;

export default {
  name: 'hgPaymentForm',
  components: {
    BaseButton,
    PaymentFormAlerts,
    PaymentFormCustomer,
    PaymentFormFinancialInstrument,
    PaymentFormFinancialInstrumentPayrix,
    PaymentFormFinancials,
  },
  props: {
    apiError: {
      type: Object,
      default: () => null,
    },
    apiLoading: {
      type: Boolean,
      default: false,
    },
    apiSuccess: {
      type: Boolean,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
    paymentDefaults: {
      type: Object,
      required: true,
    },
    paymentGateway: {
      type: Object,
      required: true,
    },
    additionalChargesSetting: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      legacyGateways: [
        'AUTHORIZENET',
        'EPROCESSING',
      ],
      model: {
        customer: {
          givenName: '',
          familyName: '',
          email: '',
          address: '',
          city: '',
          state: '',
          postalCode: '',
          phone: '',
        },
        paymentCard: {
          cvv: '',
          month: '',
          number: '',
          type: 'VISA',
          year: '',
          cardType: '',
        },
        paymentToken: '',
      },
      showErrors: false,
    };
  },
  mixins: [CurrencyFilter],
  provide() {
    return {
      $validator: this.$validator,
    };
  },
  computed: {
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    disableSubmitButton() {
      return this.apiLoading
          || this.apiSuccess
          || !this.paymentDefaults.paymentAmount;
    },
    hasErrors() {
      // Only show errors in alerts that are NOT field specific validation errors
      const fieldSpecificErrors = ['zipCode', 'email', 'phoneNumber'];
      const alertErrors = this.errors.items.filter(
        error => !fieldSpecificErrors.includes(error.field),
      );
      return !!(this.apiError || alertErrors.length > 0);
    },
    alertFormErrors() {
      // Filter out field-specific errors from alerts
      const fieldSpecificErrors = ['zipCode', 'email', 'phoneNumber'];
      return this.errors.items.filter(
        error => !fieldSpecificErrors.includes(error.field),
      );
    },
    submitButtonText() {
      return !this.disableSubmitButton ? 'SUBMIT' : 'Processing Payment';
    },
    isLegacyGateway() {
      const { gatewayId, gatewayProcessor } = this.paymentGateway.body;
      if (gatewayId && this.legacyGateways.includes(gatewayProcessor)) {
        return true;
      }
      return false;
    },
    technologyFeeFF() {
      return this.enabledFeatures[featureFlags.TECHNOLOGY_FEE];
    },
    paymentAmount() {
      if (this.technologyFeeFF && this.additionalChargesSetting.enabled) {
        return (
          ((this.paymentDefaults.paymentAmount * this.additionalChargesSetting.value) / 10000)
         + this.paymentDefaults.paymentAmount);
      }
      return this.model.paymentCard.cardType === 'CREDIT_CARD'
        ? (((this.paymentDefaults.paymentAmount * this.additionalChargesSetting.value) / 10000)
          + this.paymentDefaults.paymentAmount)
        : this.paymentDefaults.paymentAmount;
    },
  },
  methods: {
    addPaymentCardExpiration() {
      this.$validator.extend('paymentCardExpiration', {
        getMessage() {
          return 'Card date expired';
        },
        validate(value, [ccYear]) {
          const ccExpirationDate = Moment(new Date(ccYear, value - 1)).endOf('month');
          const today = Moment();
          return ccExpirationDate >= today;
        },
      });
    },
    cardTypeChange(type) {
      this.model.paymentCard.cardType = type;
      this.errors.remove('paymentCardType');
    },
    clearErrors() {
      this.$emit('clearApiError');
      this.errors.clear();
    },
    hydrateModel() {
      if (this.paymentDefaults.customer) {
        // For Payrix: only populate email, phone, and postalCode
        // For Legacy: populate all fields
        this.model.customer = {
          givenName: this.isLegacyGateway ? this.paymentDefaults.customer.givenName : '',
          familyName: this.isLegacyGateway ? this.paymentDefaults.customer.familyName : '',
          email: this.paymentDefaults.customer.primaryEmail || '',
          address: this.isLegacyGateway ? this.paymentDefaults.customer.address1 : '',
          city: this.isLegacyGateway ? this.paymentDefaults.customer.city : '',
          state: this.isLegacyGateway ? this.paymentDefaults.customer.state : '',
          postalCode: this.paymentDefaults.customer.postalCode || '',
          phone: this.paymentDefaults.customer.primaryPhone || '',
        };
      }
    },
    isPayrix() {
      return (this.paymentGateway.body.gatewayProcessor === 'PAYRIX');
    },
    save() {
      const payload = {
        folderId: this.paymentDefaults.folderId,
        payment: {
          amount: this.paymentDefaults.paymentAmount,
          email: this.model.customer.email,
          phone: this.model.customer.phone,
        },
      };

      // For Payrix: only include zip code and email/phone (no billing address)
      if (this.isPayrix()) {
        payload.payment.postalCode = this.model.customer.postalCode;
        // Billing address fields should be null for Payrix payments
        payload.payment.givenName = this.model.customer.givenName || 'null';
        payload.payment.familyName = this.model.customer.familyName || 'null';
        payload.payment.address = 'null';
        payload.payment.city = 'null';
        payload.payment.state = 'null';
      } else {
        // For legacy gateways: include all billing information
        payload.payment.givenName = this.model.customer.givenName;
        payload.payment.familyName = this.model.customer.familyName;
        payload.payment.address = this.model.customer.address;
        payload.payment.city = this.model.customer.city;
        payload.payment.state = this.model.customer.state;
        payload.payment.postalCode = this.model.customer.postalCode;
      }

      if (this.technologyFeeFF && this.additionalChargesSetting.enabled) {
        payload.payment.paymentTokenTechnologyFeePercentage
          = (this.additionalChargesSetting?.value / 100).toFixed(2);
      } else {
        payload.payment = {
          ...payload.payment,
          paymentTokenCardType: this.model.paymentCard.cardType.slice(0, -5).toLowerCase(),
          paymentTokenSurchargePercentage:
            (this.additionalChargesSetting?.value / 100).toFixed(2),
        };
      }
      if (this.isPayrix()) {
        payload.payment.paymentToken = this.model.paymentToken;
      } else {
        payload.payment.ccNumber = this.model.paymentCard.number;
        payload.payment.month = this.model.paymentCard.month;
        payload.payment.year = this.model.paymentCard.year;
        payload.payment.cvv = this.model.paymentCard.cvv;
        payload.payment.type = this.model.paymentCard.type;
      }
      this.$emit('submit', payload);
    },
    validateCardType() {
      this.errors.remove('paymentCardType');
      if (this.model.paymentCard.cardType !== '') {
        return true;
      }
      this.errors.add({
        field: 'paymentCardType',
        msg: 'Please select the type of card.',
      });
      this.$scrollTo('#payment-form--CardType');
      return false;
    },
    async submit() {
      this.clearErrors();

      // Validate card type if applicable
      if (!this.shouldValidateCardType()) return;

      // For Payrix, validate all fields at once before proceeding
      if (this.isPayrix()) {
        await this.handlePayrixPayment();
      } else {
        // For legacy gateway, validate all fields
        const isValid = await this.$validator.validateAll();
        if (!isValid) {
          this.scrollToAlerts();
          return;
        }
        this.save();
      }
    },

    shouldValidateCardType() {
      if (!this.technologyFeeFF && this.additionalChargesSetting.enabled) {
        const valid = this.validateCardType();
        if (!valid) return false;
      }
      return true;
    },

    scrollToAlerts() {
      this.$scrollTo('#payment-form--alerts');
    },

    async handlePayrixPayment() {
      this.errors.remove('paymentProcessor');
      const tokenRes = await this.$refs.paymentProcessor.getToken();

      if (tokenRes.valid) {
        this.model.paymentToken = tokenRes.data.token;
        if (tokenRes.data.customer) {
          this.model.customer.givenName = tokenRes.data.customer.first || '';
          this.model.customer.familyName = tokenRes.data.customer.last || '';
        }
        this.save();
      } else {
        const hasValidationErrors = this.errors.items.length > 0;

        if (!hasValidationErrors) {
          // Add Payrix-specific error if no field validation errors
          this.errors.add({
            field: 'paymentProcessor',
            msg: 'Please provide valid credit card information.',
          });
          this.$scrollTo('#payment-form--financial-instrument-payrix');
        }
      }
    },
  },
  mounted() {
    this.addPaymentCardExpiration();
    this.hydrateModel();
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.form-section {
  padding-bottom: 40px;

  &:last-child {
    padding-bottom: 0;
  }
}

.header {
  padding-bottom: 40px;
  padding-top: 40px;
  text-align: center;

  p {
    font-size: 1.25rem;
  }
}

h1 {
  font-size: 2.5rem;
  padding-bottom: 10px;
  text-transform: none;
}

.send-payment {
  text-align: center;
}

.payment-amount {
  color: rgba(0, 0, 0, 0.6);
  font-size: 1.786rem;
  font-weight: $typography-weight-bold;
  padding-bottom: 20px;
  padding-top: 20px;
}

.payment-amount-number {
  font-weight: $typography-weight-normal;
  font-size: 1.786rem;
  color: rgba(0, 0, 0, 0.6);
  padding-bottom: 20px;
  padding-top: 20px;
}

.submit-button {
  height: 40px;
  width: 200px;
}

.payment {
  padding-bottom: 100px;
}

.questions {
  display: block;
  font-weight: $typography-weight-bold;
  padding-top: 30px;
}

.cancel-link {
  color: $typography-color-link;
  display: inline-block;
  font-size: 0.9rem;
  font-weight: $typography-weight-bold;
  padding-top: 20px;
  text-decoration: underline;
}

hr {
  background-color: rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 0, 0, 0.15);
  border-width: 0;
  height: 1px;
  margin-bottom: 20px;
  margin-top: 20px;
  width: 100%;
}

.technology-fee-message {
  margin-bottom: 20px;
  margin-top: 20px;
}

.technology-fee-content {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  text-align: center;
}

.technology-fee-content p {
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
}
</style>
